import React, { useEffect, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  SelectChangeEvent,
} from "@mui/material";
import { CheckCircle, CloudUpload, Person, AccountBalance, Description } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { registerPartnerUser } from "../../redux/user/userSlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BiArrowBack } from "react-icons/bi";
import { LuX } from "react-icons/lu";
import { unlistedInvestorPanVerify, unlistedInvestorBankVerify } from "../../redux/admin/adminSlice";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  OFF_WHITE,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  FILTER_BORDER_DEFAULT,
  FILTER_LABEL_DEFAULT,
  INPUT_FOCUS_GLOW,
  filterFieldSx,
  modalInputSx,
  PROFILE_FONT,
} from "../../constants/layout";
import {
  BORDER,
  CARD_SHADOW,
  DETAIL_CARD_BG,
  cardSx,
  primaryButtonSx,
} from "../AssetListing/assetListingShared";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const SUCCESS_COLOR = "#08991F";

const sectionCardSx = {
  ...cardSx,
  p: 3,
};

const gridFieldSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
};

type ModalFieldProps = {
  label: string;
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  error?: string;
  touched?: boolean | string;
  disabled?: boolean;
  suffix?: React.ReactNode;
};

const ModalField = ({
  label,
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  error,
  touched,
  disabled,
  suffix,
}: ModalFieldProps) => {
  const hasError = !!(error && touched);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        component="label"
        htmlFor={id}
        sx={{
          display: "block",
          fontFamily: PROFILE_FONT,
          fontSize: 14,
          fontWeight: 500,
          color: hasError ? "#d32f2f" : DARK_NAVY,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ position: "relative" }}>
        <Box
          component="input"
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          sx={{
            ...modalInputSx,
            ...(suffix ? { pr: "72px" } : {}),
            ...(hasError
              ? {
                  borderColor: "#d32f2f",
                  "&:focus": { borderColor: "#d32f2f", boxShadow: "none" },
                }
              : {}),
          }}
        />
        {suffix ? (
          <Box
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {suffix}
          </Box>
        ) : null}
      </Box>
      {hasError ? (
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

type ModalSelectProps = {
  label: string;
  id: string;
  name: string;
  value: string;
  onChange: (e: SelectChangeEvent<string>) => void;
  onBlur?: (e: React.FocusEvent) => void;
  error?: string;
  touched?: boolean | string;
  disabled?: boolean;
  placeholder?: string;
  children: React.ReactNode;
};

const ModalSelect = ({
  label,
  id,
  name,
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled,
  placeholder = "Select...",
  children,
}: ModalSelectProps) => {
  const hasError = !!(error && touched);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        component="label"
        htmlFor={id}
        sx={{
          display: "block",
          fontFamily: PROFILE_FONT,
          fontSize: 14,
          fontWeight: 500,
          color: hasError ? "#d32f2f" : DARK_NAVY,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <FormControl fullWidth error={hasError} disabled={disabled} sx={filterFieldSx}>
        <Select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          displayEmpty
          sx={{
            "& .MuiSelect-select": {
              py: 1.25,
              px: 1.75,
              fontFamily: PROFILE_FONT,
              fontSize: 14,
              color: value ? DARK_NAVY : FILTER_LABEL_DEFAULT,
            },
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography component="span" sx={{ color: FILTER_LABEL_DEFAULT, fontSize: 14 }}>
                  {placeholder}
                </Typography>
              );
            }
            return selected as string;
          }}
        >
          {children}
        </Select>
        {hasError ? (
          <FormHelperText sx={{ fontFamily: PROFILE_FONT, fontSize: 12, mx: 0 }}>
            {error}
          </FormHelperText>
        ) : null}
      </FormControl>
    </Box>
  );
};

const validationSchema = Yup.object().shape({
  nameAsPerPan: Yup.string().required("Name as per PAN is required"),
  subBrokerCode: Yup.string().required("Sub-Broker Code is required"),
  dob: Yup.string().required("Date of Birth is required"),
  panCardNo: Yup.string().required("PAN Card No is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  bankIfsc: Yup.string().required("Bank IFSC is required"),
  bankBranch: Yup.string().required("Bank Branch is required"),
  bankAccNo: Yup.string().required("Bank Account No is required"),
  reEnterBankAccNo: Yup.string()
    .required("Re-enter Bank Account No is required")
    .oneOf([Yup.ref('bankAccNo')], 'Bank Account Numbers must match'),
  accountType: Yup.string().required("Account Type is required"),
  entityType: Yup.string().required("Entity Type is required"),
});

const entityDocuments = {
  Individual: [
    { label: "Photo", key: "photo" },
    { label: "PAN Card", key: "panCard" },
    { label: "Aadhaar Card", key: "aadhaarCard" },
    { label: "Cancel Cheque", key: "cancelCheque" },
  ],
  Corporate: [
    { label: "Copy of Balance Sheet (last 2 financial years)", key: "balanceSheet" },
    { label: "Copy of latest share-holding pattern", key: "shareholdingPattern" },
    { label: "Photograph, POI, POA, PAN and DIN number of Directors", key: "directorDocs" },
    { label: "Photograph, POI, POA, PAN of individual promoters", key: "promoterDocs" },
    { label: "Copy of Memorandum and Articles of Association", key: "moa" },
    { label: "Copy of Board Resolution", key: "boardResolution" },
    { label: "Authorized signatories list with specimen signatures", key: "signatories" },
    { label: "Shareholding pattern", key: "shareholdingPatternDoc" },
    { label: "Cancel cheque", key: "cancelCheque" },
  ],
  "Partnership Firm": [
    { label: "Copy of Balance Sheet (last 2 financial years)", key: "balanceSheet" },
    { label: "Certificate of Registration", key: "certRegistration" },
    { label: "Copy of Partnership Deed", key: "partnershipDeed" },
    { label: "Authorized signatories list with specimen signatures", key: "signatories" },
    { label: "Photograph, POI, POA, PAN of Partners", key: "partnerDocs" },
    { label: "Shareholding pattern", key: "shareholdingPattern" },
    { label: "Cancel cheque", key: "cancelCheque" },
  ],
  Trust: [
    { label: "Copy of Balance Sheet (last 2 financial years)", key: "balanceSheet" },
    { label: "Certificate of Registration", key: "certRegistration" },
    { label: "Copy of Trust Deed", key: "trustDeed" },
    { label: "List of Trustees certified by Managing Trustees / CA", key: "trusteesList" },
    { label: "Photograph, POI, POA, PAN of Trustees", key: "trusteeDocs" },
    { label: "Cancel cheque", key: "cancelCheque" },
  ],
  "HUF (Hindu Undivided Family)": [
    { label: "PAN of HUF", key: "panHUF" },
    { label: "Deed of Declaration of HUF or List of Co-Parceners", key: "huFDeed" },
    { label: "Bank Passbook / Bank statement in the name of HUF", key: "bankStatement" },
    { label: "Photograph, POI, POA, PAN of KARTA", key: "kartaDocs" },
    { label: "Photo, PAN Card, Aadhaar Card & Cancel cheque", key: "individualDocs" },
  ],
};

export type RegisterPartnerFormProps = {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  variant?: "page" | "modal";
};

const RegisterForm: React.FC<RegisterPartnerFormProps> = ({
  open = false,
  onClose,
  onSuccess,
  variant = "modal",
}: RegisterPartnerFormProps) => {
  const dispatch = useDispatch<any>();
  const formikRef = useRef<FormikProps<any>>(null);
  const navigate = useNavigate();
  const isPage = variant === "page";
  const [loading, setLoading] = useState(false);
  const [isPanVerified, setIsPanVerified] = useState(false);
  const [isBankVerified, setIsBankVerified] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const AumUserID = localStorage.getItem("AumUserID");

  function formatDateString(dateString: string): string {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  }

  const initialValues: any = {
    nameAsPerPan: "",
    subBrokerCode: "",
    dob: "",
    panCardNo: "",
    email: "",
    mobile: "",
    bankIfsc: "",
    bankBranch: "",
    bankName: "",
    bankAccNo: "",
    reEnterBankAccNo: "",
    accountType: "",
    entityType: "",
    documents: null,
    userId: AumUserID,
    userType: 6,
  };

  const resetFormState = () => {
    formikRef.current?.resetForm({ values: initialValues });
    setIsPanVerified(false);
    setIsBankVerified(false);
    setIsDragging(false);
  };

  useEffect(() => {
    if (!open && !isPage) resetFormState();
  }, [open, isPage]);

  const handleClose = () => {
    resetFormState();
    if (onClose) {
      onClose();
    } else {
      navigate("/partners");
    }
  };

  const handleSuccess = () => {
    resetFormState();
    toast.success("Partner registered successfully");
    onSuccess?.();
    if (!isPage) {
      onClose?.();
    } else {
      navigate("/partners");
    }
  };

  const handlePanVerify = async (values: any, setFieldValue: any) => {
    try {
      setLoading(true);
      if (!values.nameAsPerPan) {
        toast.error("Please enter Name as per PAN");
        setLoading(false);
        return;
      }
      if (!values.panCardNo) {
        toast.error("Please enter PAN Card Number");
        setLoading(false);
        return;
      }
      if (!values.dob) {
        toast.error("Please enter Date of Birth");
        setLoading(false);
        return;
      }
      const formattedDob = formatDateString(values.dob);
      const payload = {
        pancardNumber: values.panCardNo,
        fullName: values.nameAsPerPan,
        panName: values.nameAsPerPan,
        dateOfBirth: formattedDob,
      };
      const response = await dispatch(unlistedInvestorPanVerify(payload));
      if (response?.error?.message) {
        setLoading(false);
        toast.error(response?.payload?.message);
        return;
      }
      
      // Check 4th character of PAN to determine entity type
      const fourthChar = values.panCardNo.charAt(3).toUpperCase();
      if (fourthChar === "P") {
        // Individual
        setFieldValue("entityType", "Individual");
        toast.success("PAN verified successfully - Individual detected");
      } else {
        // Non-Individual
        toast.success("PAN verified successfully - Non-Individual detected");
      }
      
      setLoading(false);
      setIsPanVerified(true);
      setFieldValue("isPanVerified", true);
    } catch (error) {
      setLoading(false);
      console.error("Error verifying PAN:", error);
      toast.error("PAN verification failed");
    }
  };

  const checkBankIFSC = (ifscCode: string, setFieldValue: any) => {
    if (ifscCode && ifscCode.length === 11) {
      setLoading(true);
      fetch(`https://ifsc.razorpay.com/${ifscCode}`)
        .then((response) => response.json())
        .then((data: any) => {
          if (data.BANK) {
            setFieldValue("bankBranch", data.BRANCH || "");
            setFieldValue("bankName", data.BANK || "");
            toast.success("Bank details fetched successfully");
          } else {
            toast.error("No data found for this IFSC Code");
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          toast.error("Failed to fetch bank details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleBankVerify = async (values: any) => {
    try {
      setLoading(true);
      if (!values.bankAccNo || !values.bankIfsc || !values.bankName) {
        toast.error("Please fill all bank details");
        setLoading(false);
        return;
      }
      if (values.bankAccNo !== values.reEnterBankAccNo) {
        toast.error("Bank Account Numbers must match");
        setLoading(false);
        return;
      }
      const payload = {
        accountNumber: values.bankAccNo,
        ifscCode: values.bankIfsc,
        fullName: values.nameAsPerPan,
      };
      const response = await dispatch(unlistedInvestorBankVerify(payload));
      if (response?.error?.message) {
        toast.error(response?.payload?.message);
        setLoading(false);
        return;
      }
      toast.success("Bank details verified successfully!");
      setIsBankVerified(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error verifying bank details:", error);
      toast.error("Bank verification failed");
    }
  };

  const addRM = async (values: any) => {
    try {
      // Map entity type to number
      const entityTypeMap: { [key: string]: string } = {
        "Individual": "1",
        "Corporate": "2",
        "Partnership Firm": "3",
        "Trust": "4",
        "HUF (Hindu Undivided Family)": "5"
      };
      
      // Format date to DD/MM/YYYY
      const formattedDob = formatDateString(values.dob);
      
      // Create FormData to send file
      const formData = new FormData();
      formData.append("firstName", values.nameAsPerPan);
      formData.append("email", values.email);
      formData.append("mobile", values.mobile);
      formData.append("brockerCode", values.subBrokerCode);
      formData.append("userType", "2");
      formData.append("pancardNumber", values.panCardNo);
      formData.append("accountType", values.accountType);
      formData.append("bankName", values.bankName);
      formData.append("accountNumber", values.bankAccNo);
      formData.append("ifscCode", values.bankIfsc);
      formData.append("branchName", values.bankBranch);
      formData.append("dateOfBirth", formattedDob);
      formData.append("entityType", entityTypeMap[values.entityType] || "1");
      
      // Append file if it exists
      if (values.documents) {
        formData.append("file", values.documents);
      }
      
      await dispatch(registerPartnerUser(formData)).unwrap();
      handleSuccess();
    } catch (error: any) {
      const errorMessage = error.message || "An unknown error occurred";
      toast.error("Error: " + errorMessage);
    }
  };

  const formContent = (
    <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          addRM(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          handleBlur,
          errors,
          touched,
          setFieldValue,
          isSubmitting,
        }) => (
          <form
            className="w-full"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Person sx={{ fontSize: 24, color: ICON_BLUE }} />
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 600, color: DARK_NAVY }}>
                    Personal Information
                  </Typography>
                </Box>
                <Box sx={gridFieldSx}>
                  <ModalField
                    label="Name as per PAN"
                    id="nameAsPerPan"
                    name="nameAsPerPan"
                    value={values.nameAsPerPan}
                    onChange={handleChange}
                    placeholder="Enter name as per PAN"
                    error={errors.nameAsPerPan as string}
                    touched={touched.nameAsPerPan as boolean}
                  />
                  <ModalField
                    label="Sub-Broker Code"
                    id="subBrokerCode"
                    name="subBrokerCode"
                    value={values.subBrokerCode}
                    onChange={handleChange}
                    placeholder="Enter sub-broker code"
                    error={errors.subBrokerCode as string}
                    touched={touched.subBrokerCode as boolean}
                  />
                  <ModalField
                    label="Date of Birth"
                    id="dob"
                    name="dob"
                    type="date"
                    value={values.dob}
                    onChange={handleChange}
                    error={errors.dob as string}
                    touched={touched.dob as boolean}
                  />
                  <ModalField
                    label="PAN Card No"
                    id="panCardNo"
                    name="panCardNo"
                    value={values.panCardNo}
                    onChange={(e) => {
                      const pan = e.target.value.toUpperCase();
                      setFieldValue("panCardNo", pan);
                      setIsPanVerified(false);
                      if (pan.length >= 4) {
                        if (pan.charAt(3) === "P") {
                          setFieldValue("entityType", "Individual");
                        } else if (values.entityType === "Individual") {
                          setFieldValue("entityType", "");
                        }
                      }
                    }}
                    placeholder="Enter PAN card number"
                    error={errors.panCardNo as string}
                    touched={touched.panCardNo as boolean}
                    suffix={
                      isPanVerified ? (
                        <CheckCircle sx={{ color: SUCCESS_COLOR, fontSize: 22 }} />
                      ) : (
                        <Button
                          variant="text"
                          onClick={() => handlePanVerify(values, setFieldValue)}
                          sx={{
                            fontFamily: PROFILE_FONT,
                            textTransform: "none",
                            fontSize: 12,
                            minWidth: "auto",
                            p: 0,
                            color: ICON_BLUE,
                          }}
                          disabled={!values.panCardNo || !values.nameAsPerPan || !values.dob}
                        >
                          Verify
                        </Button>
                      )
                    }
                  />
                  <ModalField
                    label="Email"
                    id="email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    error={errors.email as string}
                    touched={touched.email as boolean}
                  />
                  <ModalField
                    label="Mobile Number"
                    id="mobile"
                    name="mobile"
                    value={values.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    error={errors.mobile as string}
                    touched={touched.mobile as boolean}
                  />
                </Box>
              </Box>

              <Box sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <AccountBalance sx={{ fontSize: 24, color: ICON_BLUE }} />
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 600, color: DARK_NAVY }}>
                    Bank Details
                  </Typography>
                </Box>
                <Box sx={gridFieldSx}>
                  <ModalSelect
                    label="Account Type"
                    id="accountType"
                    name="accountType"
                    value={values.accountType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Select account type"
                    error={errors.accountType as string}
                    touched={touched.accountType as boolean}
                  >
                    <MenuItem value="">
                      <em>Select Account Type</em>
                    </MenuItem>
                    <MenuItem value="Savings">Savings</MenuItem>
                    <MenuItem value="Current">Current</MenuItem>
                  </ModalSelect>
                  <Box />
                  <ModalField
                    label="Bank IFSC"
                    id="bankIfsc"
                    name="bankIfsc"
                    value={values.bankIfsc}
                    onChange={(e) => {
                      const ifscValue = e.target.value.toUpperCase();
                      setFieldValue("bankIfsc", ifscValue);
                      setIsBankVerified(false);
                      if (ifscValue.length === 11) {
                        checkBankIFSC(ifscValue, setFieldValue);
                      }
                    }}
                    placeholder="Enter bank IFSC code"
                    error={errors.bankIfsc as string}
                    touched={touched.bankIfsc as boolean}
                  />
                  <ModalField
                    label="Bank Branch"
                    id="bankBranch"
                    name="bankBranch"
                    value={values.bankBranch}
                    onChange={handleChange}
                    placeholder="Bank branch will auto-fill"
                    error={errors.bankBranch as string}
                    touched={touched.bankBranch as boolean}
                  />
                  <ModalField
                    label="Bank Account No"
                    id="bankAccNo"
                    name="bankAccNo"
                    value={values.bankAccNo}
                    onChange={(e) => {
                      handleChange(e);
                      setIsBankVerified(false);
                    }}
                    placeholder="Enter bank account number"
                    error={errors.bankAccNo as string}
                    touched={touched.bankAccNo as boolean}
                  />
                  <ModalField
                    label="Re-enter Bank Account No"
                    id="reEnterBankAccNo"
                    name="reEnterBankAccNo"
                    value={values.reEnterBankAccNo}
                    onChange={(e) => {
                      handleChange(e);
                      setIsBankVerified(false);
                    }}
                    placeholder="Re-enter bank account number"
                    error={errors.reEnterBankAccNo as string}
                    touched={touched.reEnterBankAccNo as boolean}
                    suffix={
                      isBankVerified ? (
                        <CheckCircle sx={{ color: SUCCESS_COLOR, fontSize: 22 }} />
                      ) : (
                        <Button
                          variant="text"
                          onClick={() => handleBankVerify(values)}
                          sx={{
                            fontFamily: PROFILE_FONT,
                            textTransform: "none",
                            fontSize: 12,
                            minWidth: "auto",
                            p: 0,
                            color: ICON_BLUE,
                          }}
                          disabled={!values.bankAccNo || !values.reEnterBankAccNo || !values.bankIfsc}
                        >
                          Verify
                        </Button>
                      )
                    }
                  />
                </Box>
              </Box>

              <Box sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <CloudUpload sx={{ fontSize: 24, color: ICON_BLUE }} />
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 600, color: DARK_NAVY }}>
                    Upload Documents
                  </Typography>
                </Box>
                <Box sx={{ ...gridFieldSx, mb: 3 }}>
                  <ModalSelect
                    label="Entity Type"
                    id="entityType"
                    name="entityType"
                    value={values.entityType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Select entity type"
                    error={errors.entityType as string}
                    touched={touched.entityType as boolean}
                    disabled={values.panCardNo.charAt(3)?.toUpperCase() === "P"}
                  >
                    <MenuItem value="">
                      <em>Select Entity Type</em>
                    </MenuItem>
                    <MenuItem
                      value="Individual"
                      disabled={values.panCardNo.charAt(3)?.toUpperCase() !== "P"}
                    >
                      Individual
                    </MenuItem>
                    <MenuItem value="Corporate">Corporate</MenuItem>
                    <MenuItem value="Partnership Firm">Partnership Firm</MenuItem>
                    <MenuItem value="Trust">Trust</MenuItem>
                    <MenuItem value="HUF (Hindu Undivided Family)">
                      HUF (Hindu Undivided Family)
                    </MenuItem>
                  </ModalSelect>
                </Box>
              {/* Drag and Drop Upload Area */}
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("File size must be less than 5MB");
                      return;
                    }
                    if (!file.name.endsWith('.zip')) {
                      toast.error("Please upload a ZIP file");
                      return;
                    }
                    setFieldValue("documents", file);
                  }
                }}
                sx={{
                  border: `2px dashed ${
                    isDragging ? ICON_BLUE : values.documents ? SUCCESS_COLOR : FILTER_BORDER_DEFAULT
                  }`,
                  borderRadius: "12px",
                  p: 4,
                  textAlign: "center",
                  bgcolor: isDragging ? OFF_WHITE : values.documents ? DETAIL_CARD_BG : "#FAFAFA",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                  "&:hover": {
                    borderColor: values.documents ? SUCCESS_COLOR : ICON_BLUE,
                    bgcolor: DETAIL_CARD_BG,
                  },
                  "&:focus-within": {
                    borderColor: ICON_BLUE,
                    boxShadow: INPUT_FOCUS_GLOW,
                  },
                }}
              >
                <input
                  type="file"
                  accept=".zip"
                  hidden
                  id="file-upload"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("File size must be less than 5MB");
                        e.target.value = "";
                        return;
                      }
                      if (!file.name.endsWith('.zip')) {
                        toast.error("Please upload a ZIP file");
                        e.target.value = "";
                        return;
                      }
                      setFieldValue("documents", file);
                    }
                  }}
                />
                <label htmlFor="file-upload" style={{ cursor: "pointer", display: "block" }}>
                  {values.documents ? (
                    <Box>
                      <CheckCircle sx={{ fontSize: 48, color: SUCCESS_COLOR, mb: 2 }} />
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, color: SUCCESS_COLOR, mb: 1, fontWeight: 600 }}>
                        File Uploaded Successfully!
                      </Typography>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT, mb: 1 }}>
                        {values.documents.name}
                      </Typography>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                        ({(values.documents.size / 1024).toFixed(2)} KB)
                      </Typography>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: ICON_BLUE, mt: 2 }}>
                        Click or drag to replace
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <CloudUpload sx={{ fontSize: 56, color: isDragging ? ICON_BLUE : DARK_NAVY_LIGHT, mb: 2 }} />
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, mb: 1, fontWeight: 600, color: DARK_NAVY }}>
                        {isDragging ? "Drop your file here" : "Drag & Drop your ZIP file here"}
                      </Typography>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT, mb: 2 }}>
                        or click to browse
                      </Typography>
                      <Box sx={{ ...primaryButtonSx, display: "inline-block", px: 3, py: 1 }}>
                        Choose File
                      </Box>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, display: "block", mt: 2, color: DARK_NAVY_LIGHT }}>
                        Maximum file size: 5MB • Accepted format: ZIP only
                      </Typography>
                    </Box>
                  )}
                </label>
              </Box>

              {values.entityType && (
                <Box sx={{ p: 2.5, bgcolor: DETAIL_CARD_BG, borderRadius: "12px", border: `1px solid ${BORDER}`, mt: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: PROFILE_FONT,
                      fontWeight: 600,
                      mb: 2,
                      color: DARK_NAVY,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: 14,
                    }}
                  >
                    <Description sx={{ fontSize: 18 }} />
                    Required Documents for {values.entityType}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {entityDocuments[values.entityType as keyof typeof entityDocuments]?.map((doc: any, index: number) => (
                      <Chip
                        key={index}
                        label={doc.label}
                        size="small"
                        sx={{
                          fontFamily: PROFILE_FONT,
                          bgcolor: "#fff",
                          border: `1px solid ${BORDER}`,
                          fontSize: 12,
                          height: "auto",
                          py: 0.5,
                          "& .MuiChip-label": {
                            whiteSpace: "normal",
                            padding: "6px 10px",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                {!isPage ? (
                  <Button
                    onClick={handleClose}
                    sx={{
                      fontFamily: PROFILE_FONT,
                      textTransform: "none",
                      color: DARK_NAVY_LIGHT,
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={!values.documents || isSubmitting}
                  sx={{ ...primaryButtonSx, px: 3, py: 1.25, minWidth: 140 }}
                >
                  Register Partner
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
  );

  if (isPage) {
    return (
      <>
        <AppLoader loading={loading} />
        <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", fontFamily: PROFILE_FONT }}>
        <Box
          sx={{ display: "flex", justifyContent: "flex-start", mb: 2, cursor: "pointer", color: DARK_NAVY }}
          onClick={handleClose}
        >
          <BiArrowBack size={24} />
        </Box>
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: { xs: 24, md: 28 },
            fontWeight: 700,
            color: DARK_NAVY,
            mb: 3,
          }}
        >
          Register Partner
        </Typography>
        {formContent}
        </Box>
      </>
    );
  }

  return (
    <>
      <AppLoader loading={loading} />
      <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          fontFamily: PROFILE_FONT,
          boxShadow: CARD_SHADOW,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: PROFILE_FONT,
          fontWeight: 700,
          fontSize: 20,
          color: DARK_NAVY,
          pr: 6,
          pb: 1,
        }}
      >
        Register Partner
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: 13,
            color: DARK_NAVY_LIGHT,
            fontWeight: 400,
            mt: 0.5,
          }}
        >
          Add a new partner with PAN, bank, and document details.
        </Typography>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: DARK_NAVY_LIGHT,
            "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
          }}
        >
          <LuX size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          maxHeight: "78vh",
          overflowY: "auto",
          bgcolor: OFF_WHITE,
        }}
      >
        {formContent}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RegisterForm;
